export interface TableCreationRequestTiming {
    readonly requestStartedAt: number;
    readonly requestCompletedAt: number;
}

const TABLE_CREATION_REQUEST_TIMING = Symbol('TABLE_CREATION_REQUEST_TIMING');

interface TimedTableCreationResult {
    readonly [TABLE_CREATION_REQUEST_TIMING]: TableCreationRequestTiming;
}

const hasTableCreationRequestTiming = (value: object): value is TimedTableCreationResult =>
    TABLE_CREATION_REQUEST_TIMING in value;

/**
 * Adds client-only request timing to a table-creation result. The enumerable symbol survives the
 * object spreads used by table query functions, while remaining outside backend contracts and
 * ordinary serialization.
 */
export const attachTableCreationRequestTiming = <T extends object>(value: T, timing: TableCreationRequestTiming): T => {
    Object.defineProperty(value, TABLE_CREATION_REQUEST_TIMING, {
        configurable: true,
        enumerable: true,
        value: timing,
    });
    return value;
};

/** Finds timing on either a direct result or the tuple-shaped results used by Results tables. */
export const getTableCreationRequestTiming = (value: unknown): TableCreationRequestTiming | undefined => {
    if (typeof value !== 'object' || value === null) {
        return undefined;
    }
    if (hasTableCreationRequestTiming(value)) {
        return value[TABLE_CREATION_REQUEST_TIMING];
    }
    if (Array.isArray(value)) {
        for (const item of value) {
            const timing = getTableCreationRequestTiming(item);
            if (timing) {
                return timing;
            }
        }
    }
    return undefined;
};
